import React, { useCallback, useEffect } from "react";
import { Linking, Platform, Dimensions, View, Modal } from "react-native";
import AppToast from "@/components/shared/AppToast";
import { HelpProvider } from "@/components/settings/Help";
import { ActionSheetProvider } from "@expo/react-native-action-sheet";
import {
  NavigationContainer,
  getStateFromPath,
} from "@react-navigation/native";
import { CardStyleInterpolators } from "@react-navigation/stack";
import { createAdaptiveStackNavigator } from "@/components/navigation/AdaptiveStack";
import { getRouteGroup } from "@/components/navigation/routeGroups";
import { handleBranchOpen } from "@/components/navigation/branchOpen";
import AdaptiveGroup from "@/components/layout/AdaptiveGroup";
import { ProvideBuy } from "@/components/BuyContext";
import analytics from "@/analytics";
import branch from "@/branchlib";
import { useAuth } from "@/components/AuthContext";
import { getUrlFromBranch } from "@/components/globalUtils";
import CreateEvent from "@/components/screens/Events/CreateEvent";
import EventEdit from "@/components/screens/Events/EventEdit";
import GoLive from "@/components/screens/Events/GoLive";
import EventList from "@/components/screens/Events/EventList";
import EventSubScreen from "@/components/screens/Events/EventSubScreen.js";
import EventDetails from "@/components/shared/EventDetails";
import EventGuestbook from "@/components/shared/EventGuestbook";
import EventPrivacyForm from "@/components/shared/EventPrivacyForm";
import PurchaseSuccess from "@/components/screens/Events/PurchaseSuccess";
import EventBrandingForm from "@/components/shared/EventBrandingForm";
import ManageVideo from "@/components/shared/ManageVideo";
import EventEmbedForm from "@/components/shared/EventEmbedForm";
import HowToStream from "@/components/shared/HowToStream";
import AddRoomPane from "@/components/rooms/AddRoomPane";
import BrowserStream from "@/components/screens/Events/BrowserStream";
import MultiCam from "@/components/screens/Events/MultiCam";
import SellTickets from "@/components/shared/SellTickets";
import GuestAdd from "@/components/screens/Events/GuestAdd";
import GuestImport from "@/components/screens/Events/GuestImport";
import GuestList from "@/components/screens/Events/GuestList";
import PayWall from "@/components/shared/PayWall";
import TestScreen from "@/components/screens/Events/Test";
import ForgotPassword from "@/components/screens/OnBoarding/ForgotPassword";
import ForgotPasswordLinkSent from "@/components/screens/OnBoarding/ForgotPasswordLinkSent";
import PasswordReset from "@/components/screens/OnBoarding/PasswordReset";
import OnboardingNew from "@/components/screens/OnboardingNew";
import SignUpSignIn from "@/components/screens/OnBoarding/SignUpSignIn";
import Plans from "@/components/screens/Profile/Plans";
import Profile from "@/components/screens/Profile/Profile";
import UpdateEmail from "@/components/screens/Profile/UpdateEmail";
import VideoWall from "@/components/screens/Profile/VideoWall";
import WhatsNew from "@/components/screens/Profile/WhatsNew";
import ViewerHours from "@/components/screens/Profile/ViewerHours";
import DownloadEverything from "@/components/screens/Profile/DownloadEverything";
import AskReview from "@/components/screens/AskReview";
import { LoadingScreen } from "@/components/LoadingScreen";
import EventAnalytics from "@/components/screens/Events/EventAnalytics";
import EventDonationsForm from "@/components/shared/EventDonationsForm";
import { DONATIONS_ENABLED } from "@/components/donations/config";
import { DOWNLOAD_EVERYTHING_ENABLED } from "@/components/library/config";
import { setSupportRoute } from "@/components/support/config";
import { t } from "@/localization";
import Team from "@/components/screens/Profile/Team";
import EventPeople from "@/components/screens/Events/EventPeople";
import TeamInvite from "@/components/screens/TeamInvite";
import { TEAMS_UI_ENABLED } from "@/components/teams/flag";
import { gateElement, gateScreen } from "@/components/teams/RoleGate";
import { syncWorkspaceUrl } from "@/components/teams/workspace";
import * as SplashScreen from "expo-splash-screen";
import { NativeModules } from "react-native";

export const DefaultAuthenticatedScreen = "events";

// Teams (on the web, components/teams/flag.js): in someone else's account a
// screen shows only when your role there allows it (components/teams/RoleGate.js);
// these are the components themselves while the Teams UI is off.
const BrowserStreamScreen = gateScreen(BrowserStream, "goLive");
const MultiCamScreen = gateScreen(MultiCam, "goLive");
const GuestListScreen = gateScreen(GuestList, "guests");
const GuestImportScreen = gateScreen(GuestImport, "guests");
const GuestAddScreen = gateScreen(GuestAdd, "guests");
const CreateEventScreen = gateScreen(CreateEvent, "createEvent");

// A stack navigator; on tablets, foldables and laptops it groups an event's screens
// into a menu + setting split view, centred as a modal on big screens.
const Stack = createAdaptiveStackNavigator();

const isLargeScreen = Dimensions.get("window").width >= 768;
const checkoutScreenOptions =
  Platform.OS === "android"
    ? {
        animation: "none",
        animationEnabled: false,
        cardStyleInterpolator: CardStyleInterpolators.forNoAnimation,
        gestureEnabled: false,
      }
    : {};

const getFallbackNavigationState = (routeName) => ({
  routes: [{ name: routeName }],
  index: 0,
});

const AppRouting = React.forwardRef((props, ref) => {
  const {
    isSignedOut,
    isLoading,
    signInWithTokens,
    isSigningInWithTokens,
    prefer_multiple_tests,
  } = useAuth();
  // Wide layouts: which screens share a frame (the single test gets its own).
  const routeGroupOf = useCallback(
    (route) => getRouteGroup(route, { multipleTests: !!prefer_multiple_tests }),
    [prefer_multiple_tests]
  );
  const fallbackRouteName = isSignedOut ? "SignUpSignIn" : "EventList";

  const linking = {
    prefixes: [
      "https://broadcaster.eventlive.pro",
      "http://localhost:19006",
      "http://localhost:8081",
      "eventlive://",
      "https://eventlive.app.link",
    ],

    async getInitialURL() {
      console.warn("getInitialURL called");

      // For mobile platforms
      console.warn("getInitialURL: Mobile platform detected");
      try {
        const params = await branch.getLatestReferringParams();
        console.warn(
          "getInitialURL: Branch params:",
          JSON.stringify(params, null, 2)
        );

        const url = getUrlFromBranch(params);
        if (url) {
          console.warn("getInitialURL: URL from Branch:", url);
          return url;
        }

        const linkingUrl = await Linking.getInitialURL();
        if (linkingUrl != null) {
          console.warn("getInitialURL: URL from Linking:", linkingUrl);
          return linkingUrl;
        }

        console.warn("getInitialURL: No URL found from any source");
        return null;
      } catch (error) {
        console.warn("getInitialURL: Error getting initial URL:", error);
        return null;
      }
    },

    getStateFromPath: (path, options) => {
      console.warn(
        "getStateFromPath called with path:",
        path,
        "options:",
        options
      );

      // Backward-compatible path for legacy stream delegate links.
      if (path) {
        try {
          const normalizedPath = path.startsWith("/") ? path : `/${path}`;
          const parsedUrl = new URL(
            normalizedPath,
            "https://broadcaster.eventlive.pro"
          );

          if (parsedUrl.pathname === "/streamFor") {
            const streamKey =
              parsedUrl.searchParams.get("streamKey") ||
              parsedUrl.searchParams.get("stream_key");

            if (streamKey) {
              return {
                routes: [{ name: "GoLive", params: { streamKey } }],
                index: 0,
              };
            }
          }
        } catch (error) {
          console.warn(
            "getStateFromPath: Error parsing legacy stream path",
            error
          );
        }
      }

      const state = getStateFromPath(path, options);
      console.warn("getStateFromPath: State:", state);

      // React Navigation web resets to the parsed state on browser history changes.
      // Returning undefined for "/" or unknown paths can reach BaseRouter as an
      // invalid RESET payload, so fall back to a valid root route.
      if (!state || !state.routes || state.routes.length === 0) {
        return getFallbackNavigationState(fallbackRouteName);
      }

      // Check if path contains /event or /profile and modify state to include EventList first
      if (path && (path.includes("/event") || path.includes("/profile"))) {
        if (state && state.routes && state.routes.length > 0) {
          const currentRoute = state.routes[state.routes.length - 1];

          // Check if EventList is not already the first route
          if (
            currentRoute.name !== "EventList" &&
            !(state.routes.length > 1 && state.routes[0].name === "EventList")
          ) {
            let routesToAdd = [{ name: "EventList", key: "EventList" }];

            // Check if path matches /event/{slug}/something pattern
            const eventPathMatch = path.match(/\/event\/([^\/]+)\/(.+)/);
            if (eventPathMatch) {
              const slug = eventPathMatch[1];
              const subPath = eventPathMatch[2];

              // Add EventEdit with the slug parameter if it's not the final route and not already EventEdit
              if (currentRoute.name !== "EventEdit" && subPath) {
                routesToAdd.push({
                  name: "EventEdit",
                  key: `EventEdit-${slug}`,
                  params: { slug },
                });
              }
            }

            // Create a new state with EventList (and possibly EventEdit) before the original routes
            const modifiedState = {
              ...state,
              routes: [...routesToAdd, ...state.routes],
              index: routesToAdd.length + state.routes.length - 1, // Point to the last route (original target)
            };

            console.warn(
              "getStateFromPath: Modified state with navigation stack:",
              modifiedState
            );
            return modifiedState;
          }
        }
      }

      return state;
    },

    subscribe(listener) {
      const onReceiveURL = ({ url }) => {
        console.warn("Received URL:", url);
        listener(url);
      };

      // Listen to incoming links from deep linking
      const l = Linking.addEventListener("url", onReceiveURL);

      let branchUnsubscribe;

      if (Platform.OS === "web") {
        // For web, we need to handle URL changes
        const handleUrlChange = () => {
          const url = window.location.href;

          if (url.includes("eventlive.app.link")) {
            const branchUrl = getUrlFromBranch({ $canonical_url: url });

            if (branchUrl) {
              console.warn("Web redirect to:", branchUrl);
              window.location.href = branchUrl;
              return;
            }
          }

          // For non-Branch URLs, just pass them through
          listener(url);
        };

        // Initial check
        handleUrlChange();

        // Listen for URL changes
        window.addEventListener("popstate", handleUrlChange);

        return () => {
          window.removeEventListener("popstate", handleUrlChange);
          if (l && l.remove) {
            l.remove();
          }
        };
      } else {
        branchUnsubscribe = branch.subscribe((event) =>
          handleBranchOpen(event, { signInWithTokens, openUrl: listener })
        );

        return () => {
          if (l && l.remove) {
            l.remove();
          }
          if (branchUnsubscribe) {
            branchUnsubscribe();
          }
        };
      }
    },
    config: {
      screens: {
        // Welcome: "welcome",
        SignUpSignIn: "signin",
        ForgotPassword: "forgot",
        ForgotPasswordLinkSent: "forgotsent",
        PasswordReset: "set_password",
        Test: {
          path: "getapp/:slug?",
        },
        EventList: {
          path: "list",
        },
        OnboardingNew: {
          path: "onboarding/:step?",
        },
        Profile: {
          path: "profile",
        },
        UpdateEmail: {
          path: "profile/update_email",
        },
        Plans: {
          path: "profile/plans",
        },
        VideoWall: {
          path: "profile/videowall",
        },
        WhatsNew: {
          path: "profile/whats-new",
        },
        ViewerHours: {
          path: "profile/viewer-hours",
        },
        ...(DOWNLOAD_EVERYTHING_ENABLED
          ? { DownloadEverything: { path: "profile/download-everything" } }
          : {}),
        EventEdit: {
          path: "event/:slug?",
        },
        PayWall: {
          path: "event/:slug/buy",
        },
        SellTickets: {
          path: "event/:slug/sell",
        },
        ReuploadVideo: {
          path: "event/:slug/video",
        },
        CreateEvent: {
          path: "create",
        },
        GoLive: {
          path: "stream/:streamKey?",
        },
        HowToStream: {
          path: "event/:slug/howtostream",
        },
        AddRoom: {
          path: "event/:slug/add-room",
        },
        BrowserStream: {
          path: "event/:slug/broadcast/:variant?",
        },
        MultiCam: {
          path: "event/:slug/multicam",
        },
        EventDetails: {
          path: "event/:slug/details",
        },
        ...(DONATIONS_ENABLED
          ? { EventDonations: { path: "event/:slug/fundraising" } }
          : {}),
        EventBranding: {
          path: "event/:slug/branding",
        },
        EventGuestbook: {
          path: "event/:slug/guestbook",
        },
        Embed: {
          path: "event/:slug/embed",
        },
        EventPrivacy: {
          path: "event/:slug/privacy",
        },
        GuestList: {
          path: "event/:slug/guests",
        },
        GuestImport: {
          path: "event/:slug/guests/import",
        },
        AskReview: {
          path: "review",
        },
        // SetPassword: {
        //   path: "set_password",
        // },
        GuestAdd: {
          path: "event/:slug/guests/invite",
        },
        EventAnalytics: {
          path: "event/:slug/analytics",
        },
        PurchaseSuccess: {
          path: "event/:slug/success",
        },
        ...(TEAMS_UI_ENABLED
          ? {
              Team: { path: "profile/team" },
              EventPeople: { path: "event/:slug/people" },
              TeamInvite: { path: "invite/:token" },
            }
          : {}),
      },
    },
  };

  const routeNameRef = React.useRef();

  useEffect(() => {
    if (typeof window !== "undefined" && window && window.addEventListener) {
      // Handle keyboard events
      const handleKeyDown = (event) => {
        // Check for backspace or escape keys
        if (event.key === "Backspace" || event.key === "Escape") {
          ref.goBack();
        }
      };

      window.addEventListener("keydown", handleKeyDown);

      return () => {
        window.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [ref]);

  return (
    <ActionSheetProvider>
      <HelpProvider>
        {isSigningInWithTokens && (
          <Modal
            transparent={true}
            animationType="fade"
            visible={isSigningInWithTokens}
            statusBarTranslucent={true}
          >
            <View
              style={{
                flex: 1,
                backgroundColor:
                  Platform.OS === "web" ? "rgba(255, 255, 255, 0.95)" : "white",
              }}
            >
              <LoadingScreen />
            </View>
          </Modal>
        )}
        {/* {isLoading && <LoadingScreen />}
        {!isLoading && ( */}
        <NavigationContainer
          linking={linking}
          ref={ref}
          fallback={<LoadingScreen />}
          onReady={() => {
            const current = ref.current.getCurrentRoute().name;
            analytics.page(current);
            routeNameRef.current = current;
            setSupportRoute(current);

            // Check for pending deep links on iOS
            if (Platform.OS === "ios" && NativeModules.StartStream) {
              NativeModules.StartStream.checkPendingDeepLink()
                .then((pendingStreamKey) => {
                  if (pendingStreamKey) {
                    console.log("Found pending stream key:", pendingStreamKey);
                    // Load and stream for the pending key
                    NativeModules.StartStream.loadAndStreamFor(pendingStreamKey)
                      .then(() => {
                        console.log("Pending stream started successfully");
                      })
                      .catch((error) => {
                        console.error("Failed to start pending stream:", error);
                      });
                  }
                })
                .catch((error) => {
                  console.error("Error checking pending deep link:", error);
                });
            }
          }}
          onStateChange={async (state) => {
            const previousRouteName = routeNameRef.current;
            const currentRouteName = ref.current.getCurrentRoute().name;

            if (previousRouteName !== currentRouteName) {
              analytics.page(currentRouteName);
            } else {
              // Track Onboarding steps
              const lastRoute = state.routes[state.routes.length - 1];
              // console.log("LAST ROUT", lastRoute);
              if (lastRoute?.name === "OnboardingNew") {
                analytics.page("Onboarding_" + lastRoute?.params?.step);
              }
            }

            // Save the current route name for later comparison
            routeNameRef.current = currentRouteName;
            setSupportRoute(currentRouteName);
            // Teams: React Navigation has rewritten the address; put
            // ?as=<owner> back while working in someone else's account.
            if (TEAMS_UI_ENABLED) setTimeout(syncWorkspaceUrl, 0);
          }}
        >
          <ProvideBuy>
            <Stack.Navigator
              name="Root"
              getRouteGroup={isSignedOut ? undefined : routeGroupOf}
              GroupComponent={AdaptiveGroup}
              // Must name a screen registered for the current auth state: the navigator
              // mounts only once linking's getInitialURL (Branch) resolves, and if auth has
              // settled to signed out by then a missing "EventList" throws at launch.
              initialRouteName={isSignedOut ? "OnboardingNew" : "EventList"}
              screenOptions={{
                animationEnabled: true,
                headerShown: false,
                presentation: "card",
                // animation: "none",
              }}
            >
              {isSignedOut && (
                <>
                  <Stack.Screen
                    name="OnboardingNew"
                    component={OnboardingNew}
                    options={{
                      headerShown: false,
                      title: t("Welcome to EventLive"),
                      // presentation: "transparentModal", //"transparentModal",
                      animationEnabled: false,
                      gestureEnabled: true,
                    }}
                    getId={({ params }) => params?.step}
                  />
                  <Stack.Screen
                    name="SignUpSignIn"
                    component={SignUpSignIn}
                    options={{
                      headerShown: false,
                      title: t("Sign In - EventLive"),
                      animationEnabled: false,
                    }}
                  />

                  <Stack.Screen
                    name="ForgotPassword"
                    component={ForgotPassword}
                    options={{
                      headerShown: false,
                      title: t("Forgot Password - EventLive"),
                    }}
                  />
                  <Stack.Screen
                    name="ForgotPasswordLinkSent"
                    component={ForgotPasswordLinkSent}
                    options={{
                      headerShown: false,
                      title: t("Password Restore Link Sent - EventLive"),
                    }}
                  />
                </>
              )}
              {!isSignedOut && (
                <>
                  <Stack.Screen
                    name="EventList"
                    component={EventList}
                    options={{
                      headerShown: false,
                      title: t("My Events"),
                      animationEnabled: false,
                    }}
                  />
                  <Stack.Group
                    screenOptions={{
                      headerShown: false,
                      // presentation: isLargeScreen ? "transparentModal" : "card", // todo: default?
                      presentation: "card",
                      headerTransparent: false,
                      // cardStyle: isLargeScreen
                      //   ? {
                      //       overflow: "hidden",
                      //       borderRadius: 18,
                      //       marginVertical: "3%",
                      //       marginHorizontal: "auto",
                      //       opacity: 1,
                      //       maxWidth: 720,
                      //       height: "94%",
                      //       width: "100%",
                      //       minHeight: "auto",
                      //       backgroundColor: "transparent",
                      //     }
                      //   : {},
                      // cardStyleInterpolator: isLargeScreen
                      //   ? CardStyleInterpolators.forModalPresentationIOS
                      //   : undefined,
                      animationEnabled: !isLargeScreen,
                      gestureEnabled: true,
                      cardOverlayEnabled: isLargeScreen,
                      detachPreviousScreen: false,
                    }}
                  >
                    <Stack.Screen
                      name="EventEdit"
                      component={EventEdit}
                      options={{
                        headerShown: false,
                        title: t("Event"),
                      }}
                    />
                    <Stack.Screen
                      name="EventDetails"
                      options={{
                        headerShown: false,
                        title: t("Event Details"),
                        gestureDirection: "horizontal",
                      }}
                    >
                      {(props) =>
                        gateElement(
                          "editEvent",
                          props,
                          <EventSubScreen
                            {...props}
                            contentComponent={EventDetails}
                            title={t("Details")}
                          />
                        )
                      }
                    </Stack.Screen>

                    <Stack.Screen
                      name="ReuploadVideo"
                      options={{
                        title: t("Manage Video"),
                        gestureDirection: "horizontal",
                      }}
                    >
                      {(props) =>
                        gateElement(
                          "video",
                          props,
                          <EventSubScreen
                            {...props}
                            contentComponent={ManageVideo}
                            title={t("Manage Video")}
                          />
                        )
                      }
                    </Stack.Screen>
                    <Stack.Screen
                      name="HowToStream"
                      options={{
                        title: t("How to Go Live"),
                        gestureDirection: "horizontal",
                      }}
                    >
                      {(props) =>
                        gateElement(
                          "goLive",
                          props,
                          <EventSubScreen
                            {...props}
                            contentComponent={HowToStream}
                            title={t("How to Go Live")}
                          />
                        )
                      }
                    </Stack.Screen>
                    <Stack.Screen
                      name="BrowserStream"
                      component={BrowserStreamScreen}
                      options={{
                        title: t("Go Live"),
                        gestureDirection: "horizontal",
                      }}
                    />
                    <Stack.Screen
                      name="MultiCam"
                      component={MultiCamScreen}
                      options={{
                        title: t("Multi-Camera"),
                        gestureDirection: "horizontal",
                      }}
                    />
                    {DONATIONS_ENABLED && (
                      <Stack.Screen
                        name="EventDonations"
                        options={{
                          title: t("Fundraising"),
                          gestureDirection: "horizontal",
                        }}
                      >
                        {(props) =>
                          gateElement(
                            "fundraising",
                            props,
                            <EventSubScreen
                              {...props}
                              contentComponent={EventDonationsForm}
                              title={t("Fundraising")}
                            />
                          )
                        }
                      </Stack.Screen>
                    )}
                    <Stack.Screen
                      name="AddRoom"
                      options={{
                        title: t("Add a room"),
                        gestureDirection: "horizontal",
                      }}
                    >
                      {(props) => (
                        <EventSubScreen
                          {...props}
                          contentComponent={AddRoomPane}
                          title={t("Add a room")}
                        />
                      )}
                    </Stack.Screen>
                    <Stack.Screen
                      name="EventPrivacy"
                      options={{
                        title: t("Privacy"),
                        gestureDirection: "horizontal",
                      }}
                    >
                      {(props) =>
                        gateElement(
                          "editEvent",
                          props,
                          <EventSubScreen
                            {...props}
                            contentComponent={EventPrivacyForm}
                            title={t("Privacy")}
                          />
                        )
                      }
                    </Stack.Screen>
                    <Stack.Screen
                      name="EventBranding"
                      options={{
                        title: t("Branding"),
                        gestureDirection: "horizontal",
                      }}
                    >
                      {(props) =>
                        gateElement(
                          "editEvent",
                          props,
                          <EventSubScreen
                            {...props}
                            contentComponent={EventBrandingForm}
                            title={t("Branding")}
                          />
                        )
                      }
                    </Stack.Screen>
                    <Stack.Screen
                      name="EventGuestbook"
                      options={{
                        title: t("Guestbook"),
                        gestureDirection: "horizontal",
                      }}
                    >
                      {(props) =>
                        gateElement(
                          "moderate",
                          props,
                          <EventSubScreen
                            {...props}
                            contentComponent={EventGuestbook}
                            title={t("Guestbook")}
                          />
                        )
                      }
                    </Stack.Screen>
                    <Stack.Screen
                      name="Embed"
                      options={{
                        title: t("Embed"),
                        gestureDirection: "horizontal",
                      }}
                    >
                      {(props) =>
                        gateElement(
                          "editEvent",
                          props,
                          <EventSubScreen
                            {...props}
                            contentComponent={EventEmbedForm}
                            title={t("Embed")}
                          />
                        )
                      }
                    </Stack.Screen>

                    <Stack.Screen
                      name="PayWall"
                      options={{
                        ...checkoutScreenOptions,
                        title: t("Unlock event"),
                      }}
                    >
                      {(props) =>
                        gateElement(
                          "buy",
                          props,
                          <EventSubScreen
                            {...props}
                            contentComponent={PayWall}
                            backButtonLite
                            title={t("Unlock event")}
                          />
                        )
                      }
                    </Stack.Screen>
                    <Stack.Screen
                      name="GuestList"
                      component={GuestListScreen}
                      options={{
                        headerShown: false,
                        title: t("Guest List"),
                        gestureDirection: "horizontal",
                      }}
                    />
                    <Stack.Screen
                      name="GuestImport"
                      component={GuestImportScreen}
                      options={{
                        headerShown: false,
                        title: t("Import Guests"),
                        gestureDirection: "horizontal",
                      }}
                    />
                    <Stack.Screen
                      name="GuestAdd"
                      component={GuestAddScreen}
                      options={{
                        headerShown: false,
                        title: t("Add a Guest"),
                        gestureDirection: "horizontal",
                      }}
                    />

                    <Stack.Screen
                      name="EventAnalytics"
                      options={{
                        headerShown: false,
                        title: t("Event Analytics"),
                        gestureDirection: "horizontal",
                      }}
                    >
                      {(props) =>
                        gateElement(
                          "analytics",
                          props,
                          <EventSubScreen
                            {...props}
                            contentComponent={EventAnalytics}
                            title={t("Event Analytics")}
                          />
                        )
                      }
                    </Stack.Screen>

                    <Stack.Screen
                      name="PurchaseSuccess"
                      options={{
                        ...checkoutScreenOptions,
                        headerShown: false,
                        title: t("Purchase Successful"),
                        gestureDirection: "horizontal",
                      }}
                    >
                      {(props) =>
                        gateElement(
                          "buy",
                          props,
                          <EventSubScreen
                            {...props}
                            contentComponent={PurchaseSuccess}
                            backButtonLite
                            title={t("Purchase Successful")}
                          />
                        )
                      }
                    </Stack.Screen>

                    <Stack.Screen
                      name="SellTickets"
                      options={{
                        title: t("Sell Tickets"),
                        gestureDirection: "horizontal",
                      }}
                    >
                      {(props) =>
                        gateElement(
                          "sellTickets",
                          props,
                          <EventSubScreen
                            {...props}
                            contentComponent={SellTickets}
                            title={t("Sell Tickets")}
                          />
                        )
                      }
                    </Stack.Screen>

                    <Stack.Screen
                      name="Test"
                      component={TestScreen}
                      options={{
                        headerShown: false,
                        title: t("Test EventLive"),
                      }}
                    />

                    {TEAMS_UI_ENABLED && (
                      <Stack.Screen
                        name="EventPeople"
                        options={{
                          title: "People",
                          gestureDirection: "horizontal",
                        }}
                      >
                        {(props) =>
                          gateElement(
                            "manageTeam",
                            props,
                            <EventSubScreen
                              {...props}
                              contentComponent={EventPeople}
                              title={t("People")}
                            />
                          )
                        }
                      </Stack.Screen>
                    )}
                  </Stack.Group>

                  <Stack.Screen
                    name="CreateEvent"
                    component={CreateEventScreen}
                    options={{ headerShown: false, title: t("Schedule Event") }}
                  />

                  <Stack.Screen
                    name="Plans"
                    component={Plans}
                    options={{
                      ...checkoutScreenOptions,
                      // ...modalOptions,
                      headerShown: false,
                      title: t("EventLive Plans"),
                    }}
                  />

                  <Stack.Screen
                    name="VideoWall"
                    component={VideoWall}
                    options={{
                      // ...modalOptions,
                      headerShown: false,
                      title: t("Video Wall"),
                    }}
                  />
                  <Stack.Screen
                    name="Profile"
                    component={Profile}
                    options={{ headerShown: false, title: t("Profile") }}
                  />
                  {TEAMS_UI_ENABLED && (
                    <Stack.Screen
                      name="Team"
                      component={Team}
                      options={{ headerShown: false, title: "Team" }}
                    />
                  )}
                  <Stack.Screen
                    name="WhatsNew"
                    component={WhatsNew}
                    options={{ headerShown: false, title: t("What's new") }}
                  />
                  <Stack.Screen
                    name="ViewerHours"
                    component={ViewerHours}
                    options={{ headerShown: false, title: t("Viewer-hours") }}
                  />
                  {DOWNLOAD_EVERYTHING_ENABLED && (
                    <Stack.Screen
                      name="DownloadEverything"
                      component={DownloadEverything}
                      options={{
                        headerShown: false,
                        title: t("Download everything"),
                      }}
                    />
                  )}
                  <Stack.Screen
                    name="UpdateEmail"
                    component={UpdateEmail}
                    options={{
                      // ...modalOptions,
                      headerShown: false,
                      title: t("Update Email"),
                    }}
                  />
                  {/* Duplicated here  */}
                  <Stack.Screen
                    name="ForgotPassword"
                    component={ForgotPassword}
                    options={{
                      headerShown: false,
                      title: t("Forgot Password - EventLive"),
                    }}
                  />
                  <Stack.Screen
                    name="ForgotPasswordLinkSent"
                    component={ForgotPasswordLinkSent}
                    options={{
                      headerShown: false,
                      title: t("Password Restore Link Sent - EventLive"),
                    }}
                  />
                </>
              )}
              <Stack.Screen
                name="AskReview"
                component={AskReview}
                options={{ headerShown: false, title: t("Help Us Grow") }}
              />

              <Stack.Screen
                name="GoLive"
                component={GoLive}
                options={{ headerShown: false, title: t("Go Live") }}
              />

              {/* Teams: the invite page works signed in and signed out (it
                  stays on screen while you sign in from it). */}
              {TEAMS_UI_ENABLED && (
                <Stack.Screen
                  name="TeamInvite"
                  component={TeamInvite}
                  options={{
                    headerShown: false,
                    title: t("Team invite - EventLive"),
                  }}
                />
              )}

              <Stack.Screen
                name="PasswordReset"
                component={PasswordReset}
                options={{
                  headerShown: false,
                  title: t("Set a New Password - EventLive"),
                }}
              />
            </Stack.Navigator>
          </ProvideBuy>
        </NavigationContainer>
        {/* )} */}
        <AppToast />
      </HelpProvider>
    </ActionSheetProvider>
  );
});

export default AppRouting;
